import {PedagogicalLevelFields} from './PedagogicalLevelFields';
import {resolveCurriculum, curriculumChapterNumber} from './curriculum';
import { useEffect, useMemo, useState } from "react";
import { ArrowLeft, BookOpen, Plus, RotateCcw, Save, Trash2, X } from "lucide-react";
import { SchoolLevel } from "./catalog";
import { Curriculum, cloneCurriculum, defaultCurriculum, newCurriculumId } from "./curriculum";

const programLevels: SchoolLevel[] = ["Seconde", "1re Spé", "Tle Spé", "1re ES", "Tle ES"];

type AuthResult = { user?: { role?: string } };
type CurriculumResult = { curriculum: Curriculum | null; source?: string; managedByAkyo?: boolean; studioUrl?: string; stale?: boolean; taxonomyRevision?: number };

async function api<T>(path: string, options?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    credentials: "include",
    headers: { "Content-Type": "application/json", ...(options?.headers ?? {}) },
    ...options,
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(body.error ?? "Une erreur est survenue");
  return body as T;
}

export default function CurriculumSettingsApp() {
  const [level, setLevel] = useState<SchoolLevel>("Seconde");
  const [curriculum, setCurriculum] = useState<Curriculum>(() => cloneCurriculum(defaultCurriculum));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [authorized, setAuthorized] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [managedByAkyo, setManagedByAkyo] = useState(false);
  const [studioUrl, setStudioUrl] = useState("https://api.akyo.online/studio/taxonomy");
  const [taxonomyRevision, setTaxonomyRevision] = useState<number | null>(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  useEffect(() => {
    void (async () => {
      try {
        const auth = await api<AuthResult>("/api/auth/me");
        if (auth.user?.role !== "admin") { setAuthorized(false); return; }
        setAuthorized(true);
        const result = await api<CurriculumResult>("/api/curriculum");
        const central = result.managedByAkyo === true || String(result.source ?? "").startsWith("akyo");
        setManagedByAkyo(central);
        if (result.studioUrl) setStudioUrl(result.studioUrl);
        setTaxonomyRevision(typeof result.taxonomyRevision === "number" ? result.taxonomyRevision : null);
        if (result.curriculum && Object.keys(result.curriculum).length) {
          setCurriculum(central ? result.curriculum : resolveCurriculum(result.curriculum));
        } else if (!central) {
          const seeded = cloneCurriculum(defaultCurriculum);
          setCurriculum(seeded);
          await api("/api/curriculum", { method: "PUT", body: JSON.stringify({ curriculum: seeded }) });
        }
      } catch (reason) {
        setError(reason instanceof Error ? reason.message : "Chargement impossible");
      } finally { setLoading(false); }
    })();
  }, []);

  const themes = curriculum[level] ?? [];
  const chapterCount = useMemo(() => themes.reduce((sum, item) => sum + item.chapters.length, 0), [themes]);

  function updateLevelThemes(nextThemes: NonNullable<Curriculum[SchoolLevel]>) {
    setCurriculum((current) => ({ ...current, [level]: nextThemes }));
    setDirty(true);
    setNotice("");
  }

  function renameTheme(themeId: string, title: string) {
    updateLevelThemes(themes.map((item) => item.id === themeId ? { ...item, title } : item));
  }

  function addTheme() {
    updateLevelThemes([...themes, { id: newCurriculumId(`${level}-theme`), title: "Nouveau thème", chapters: [] }]);
  }

  function deleteTheme(themeId: string) {
    const target = themes.find((item) => item.id === themeId);
    if (!target) return;
    const message = target.chapters.length
      ? `Supprimer le thème « ${target.title} » et ses ${target.chapters.length} chapitres du programme ?\n\nLes anciennes activités et questions déjà enregistrées ne seront pas supprimées.`
      : `Supprimer le thème « ${target.title} » du programme ?`;
    if (!window.confirm(message)) return;
    updateLevelThemes(themes.filter((item) => item.id !== themeId));
  }

  function addChapter(themeId: string) {
    updateLevelThemes(themes.map((item) => item.id === themeId
      ? { ...item, chapters: [...item.chapters, { id: newCurriculumId(`${themeId}-chapter`), title: "Nouveau chapitre" }] }
      : item));
  }

  function renameChapter(themeId: string, chapterId: string, title: string) {
    updateLevelThemes(themes.map((item) => item.id === themeId
      ? { ...item, chapters: item.chapters.map((entry) => entry.id === chapterId ? { ...entry, title } : entry) }
      : item));
  }

  function deleteChapter(themeId: string, chapterId: string) {
    const targetTheme = themes.find((item) => item.id === themeId);
    const target = targetTheme?.chapters.find((item) => item.id === chapterId);
    if (!target || !window.confirm(`Supprimer « ${target.title} » du programme ?\n\nLes anciennes activités et questions déjà enregistrées restent conservées.`)) return;
    updateLevelThemes(themes.map((item) => item.id === themeId
      ? { ...item, chapters: item.chapters.filter((entry) => entry.id !== chapterId) }
      : item));
  }

  function resetLevel() {
    if (!window.confirm(`Réinitialiser le programme de ${level} avec le contenu d’origine ?`)) return;
    const defaults = cloneCurriculum(defaultCurriculum)[level] ?? [];
    updateLevelThemes(defaults);
  }

  async function save() {
    setError(""); setNotice("");
    try {
      setSaving(true);
      const cleaned = cloneCurriculum(curriculum);
      for (const [schoolLevel, schoolThemes] of Object.entries(cleaned)) {
        cleaned[schoolLevel as SchoolLevel] = (schoolThemes ?? [])
          .map((item) => ({ ...item, title: item.title.trim(), chapters: item.chapters.map((entry) => ({ ...entry, title: entry.title.trim() })).filter((entry) => entry.title) }))
          .filter((item) => item.title);
      }
      await api("/api/curriculum", { method: "PUT", body: JSON.stringify({ curriculum: cleaned }) });
      setCurriculum(cleaned);
      setDirty(false);
      setNotice("Programmes enregistrés.");
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Enregistrement impossible");
    } finally { setSaving(false); }
  }

  if (loading) return <main className="curriculum-state"><div className="loading-ring" /><p>Chargement des programmes…</p></main>;
  if (!authorized) return <main className="curriculum-state"><BookOpen size={34} /><h1>Accès réservé à l’administrateur</h1><a href="/?view=settings">Retour aux paramètres</a></main>;

  if (managedByAkyo) return <div className="curriculum-shell">
    <header className="curriculum-topbar">
      <div><a href="/?view=settings" className="curriculum-back"><ArrowLeft size={18} /> Retour aux paramètres</a><div><span>Paramètres</span><h1>Programmes et thèmes</h1></div></div>
    </header>
    <main className="curriculum-page">
      <section className="curriculum-heading">
        <div>
          <span>Référentiel central Akyo</span>
          <h2>Programmes synchronisés</h2>
          <p>Les niveaux, matières, thèmes et chapitres sont désormais créés une seule fois dans Akyo Studio puis réutilisés dans Competenceo.</p>
        </div>
        {taxonomyRevision!==null&&<div className="curriculum-counts"><strong>{taxonomyRevision}</strong><small>révision Akyo</small></div>}
      </section>
      <section className="curriculum-theme">
        <header><span>Source unique</span><strong>Akyo Studio</strong></header>
        <div className="curriculum-chapters">
          <p>Pour ajouter, renommer ou archiver un thème ou un chapitre, utilise Akyo Studio. Les activités Competenceo restent ici et conservent leurs anciens choix.</p>
          <a className="curriculum-save" href={studioUrl} target="_blank" rel="noreferrer">Ouvrir Akyo Studio</a>
        </div>
      </section>
      <p className="curriculum-history-note">Competenceo lit le référentiel via son backend. Aucun token Akyo n’est envoyé au navigateur.</p>
    </main>
  </div>;

  return <div className="curriculum-shell">
    <header className="curriculum-topbar">
      <div><a href="/?view=settings" className="curriculum-back"><ArrowLeft size={18} /> Retour aux paramètres</a><div><span>Paramètres</span><h1>Programmes et thèmes</h1></div></div>
      <button className="curriculum-save" disabled={!dirty || saving} onClick={() => void save()}><Save size={17} /> {saving ? "Enregistrement…" : dirty ? "Enregistrer" : "Enregistré"}</button>
    </header>

    {(error || notice) && <div className={`curriculum-toast ${error ? "error" : "success"}`}><span>{error || notice}</span><button onClick={() => { setError(""); setNotice(""); }}><X size={16} /></button></div>}

    <main className="curriculum-page">
      <section className="curriculum-heading">
        <div><span>Organisation pédagogique</span><h2>Programmes</h2><p>Ces thèmes et chapitres servent de référence pour les activités et les QCM.</p></div>
        <div className="curriculum-counts"><strong>{themes.length}</strong><small>thèmes</small><strong>{chapterCount}</strong><small>chapitres</small></div>
      </section>

      <nav className="curriculum-levels" aria-label="Niveau">
        <PedagogicalLevelFields value={level} levels={programLevels} onChange={value=>setLevel(value as SchoolLevel)} />
      </nav>

      <div className="curriculum-actions">
        <button onClick={addTheme}><Plus size={16} /> Ajouter un thème</button>
        <button onClick={resetLevel}><RotateCcw size={16} /> Réinitialiser {level}</button>
      </div>

      <section className="curriculum-theme-list">
        {themes.map((item, themeIndex) => <article className="curriculum-theme" key={item.id}>
          <header>
            <span>Thème {themeIndex + 1}</span>
            <input value={item.title} onChange={(event) => renameTheme(item.id, event.target.value)} aria-label={`Nom du thème ${themeIndex + 1}`} />
            <button className="delete" onClick={() => deleteTheme(item.id)} title="Supprimer le thème"><Trash2 size={17} /></button>
          </header>
          <div className="curriculum-chapters">
            {item.chapters.map((entry, chapterIndex) => <div className="curriculum-chapter" key={entry.id}>
              <b>{curriculumChapterNumber(entry) ?? chapterIndex + 1}</b>
              <input value={entry.title} onChange={(event) => renameChapter(item.id, entry.id, event.target.value)} aria-label={`Chapitre ${chapterIndex + 1}`} />
              <button className="delete" onClick={() => deleteChapter(item.id, entry.id)} title="Supprimer le chapitre"><Trash2 size={15} /></button>
            </div>)}
            {!item.chapters.length && <p className="curriculum-empty">Aucun chapitre dans ce thème.</p>}
            <button className="add-chapter" onClick={() => addChapter(item.id)}><Plus size={15} /> Ajouter un chapitre</button>
          </div>
        </article>)}
        {!themes.length && <div className="curriculum-empty-page"><BookOpen size={30} /><strong>Aucun thème pour {level}</strong><button onClick={addTheme}><Plus size={16} /> Ajouter le premier thème</button></div>}
      </section>

      <p className="curriculum-history-note">La suppression d’un thème ou d’un chapitre enlève ce choix pour les nouvelles préparations. Les anciennes activités, questions QCM et notes restent conservées dans l’historique.</p>
    </main>
  </div>;
}
